import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FocusTrap from 'focus-trap-react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import ExpressPaymentsMinicart from '@components/bag/ExpressPaymentsMinicart';
import { MinicartActionTypes } from '@state/features/common.types';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import Button from '@stone/ui/button/Button';
import MiniBagItem from '@stone/components/minibag/MiniBagItem';
import ProductRecommendation from '@stone/components/product/ProductRecommendation';
import BagPaymentLogos from '@stone/components/bag/BagPaymentsLogos';
import { addURLParameter, siteUrl } from '@common/utils/urlutils';
import { getId } from '@common/utils/brandDiffsUtils';
import analytics from '@stone/utils/analytics';
import { useLangChecker } from '@common/utils/i18n';
const LoginOrRegisterColumn = React.lazy(() => import('@stone/components/login/LoginOrRegisterColumn'));
const BagInfoBox = React.lazy(() => import('@stone/components/bag/BagInfoBox'));
export const getFocusableEl = (parent, which) => {
    const focusableEls = Array.from(parent.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
    const actualFocusableEls = focusableEls.filter((el) => window.getComputedStyle(el).display != 'none');
    if (which == 'first') {
        return actualFocusableEls[0];
    }
    else {
        return actualFocusableEls[actualFocusableEls.length - 1];
    }
};
const MiniBagModal = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [modalExpressPaymentsOpen, setModalExpressPaymentsOpen] = useState(false);
    const [modalLoginMinicart, setModalLoginMinicart] = useState(false);
    const [loaded, setLoaded] = useState(0);
    const buttonsRef = useRef();
    const expressCtaRef = useRef();
    const wrapper = useRef(null);
    const [buttonsHeight, setButtonsHeight] = useState();
    const [forceReload, setForceReload] = useState(false);
    const { minicart } = useSelector((state) => state.minicart);
    const { isDesktop } = useMediaQueries();
    const isOpen = useMemo(() => {
        if (loaded >= 3) {
            if (modalExpressPaymentsOpen) {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            return false;
        }
    }, [modalExpressPaymentsOpen, loaded]);
    useEffect(() => {
        if (modalExpressPaymentsOpen) {
            dispatch({ type: MinicartActionTypes.HOVERING_MINICART });
        }
    }, [modalExpressPaymentsOpen]);
    const updateExpressButtonsHeight = () => {
        var _a;
        if (isOpen) {
            setTimeout(() => {
                var _a;
                let express = (_a = buttonsRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.minibag__expresspayment');
                setButtonsHeight(express === null || express === void 0 ? void 0 : express.offsetHeight);
            }, 200);
        }
        else {
            const wrapper = (_a = buttonsRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.minibag__buttons__wrapper');
            setButtonsHeight(wrapper === null || wrapper === void 0 ? void 0 : wrapper.offsetHeight);
        }
    };
    useEffect(() => {
        updateExpressButtonsHeight();
    }, [isOpen]);
    const updateBag = () => {
        dispatch({ type: MinicartActionTypes.UPDATE_MINICART });
    };
    if (!minicart) {
        return React.createElement(React.Fragment, null);
    }
    if (minicart.numItems === 0) {
        return (React.createElement("div", { className: "minibag__empty" },
            React.createElement("h2", { className: "minibag__empty--title" }, t('bag.emptymessage')),
            React.createElement(ProductRecommendation, { slot: 'cart-recommendations-empty', limit: 3 })));
    }
    const buttonLogin = () => {
        isDesktop ? setModalLoginMinicart(true) : props.onLoginClick();
        // analytics.expressCheckoutLoginRegister('mini bag');
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) }, modalLoginMinicart && (React.createElement(FocusTrap, { focusTrapOptions: {
                onDeactivate: () => setModalLoginMinicart(false),
                fallbackFocus: () => wrapper.current,
                initialFocus: () => wrapper.current,
                clickOutsideDeactivates: true,
            }, active: modalLoginMinicart },
            React.createElement("div", { ref: wrapper, className: "minibag-login", "aria-modal": modalLoginMinicart, role: "dialog", tabIndex: -1 },
                React.createElement(motion.div, { transition: { type: 'tween', duration: 0.3, ease: [0.25, 1, 0.5, 1] }, initial: { y: '100%', opacity: 0 }, animate: { y: 0, opacity: 1 } },
                    React.createElement("div", { className: "minibag-login__content" },
                        React.createElement(LoginOrRegisterColumn, Object.assign({}, props, { onBack: () => setModalLoginMinicart(false), closeModal: () => {
                                setModalLoginMinicart(false);
                                props.onClose();
                                if (forceReload) {
                                    window.location.href = addURLParameter(window.location.href, 'openMiniCart', 'true');
                                }
                            }, from: 'minibag', setForceReload: setForceReload, linkreload: `${window.location.href}?openMiniCart=true`, componentContext: "drawer", hideElements: ['wishlistbutton', 'benefits'] })))))))),
        React.createElement("div", { className: "scrollable-inner" },
            React.createElement("div", { className: "container-minibag-up" },
                React.createElement("ul", { className: "d-none d-sm-block minibag__list" }, [...minicart.items].map((i) => (React.createElement(MiniBagItem, { updateWishlist: props === null || props === void 0 ? void 0 : props.updateWishlist, key: i.UUID, onRemove: () => updateBag(), showLoginModal: props.onLoginClick, item: i })))),
                React.createElement("ul", { className: "d-block d-sm-none minibag__mobile" },
                    React.createElement(MiniBagItem, { updateWishlist: props === null || props === void 0 ? void 0 : props.updateWishlist, key: minicart.items[minicart.items.length - 1].UUID, onRemove: () => updateBag(), item: minicart.items[minicart.items.length - 1], showLoginModal: props.onLoginClick })),
                ((_a = minicart.items) === null || _a === void 0 ? void 0 : _a.length) > 1 && (React.createElement("a", { className: "d-block d-sm-none minibag__mobile-label", href: siteUrl('cartUrl') },
                    React.createElement("p", null, `+${((_b = minicart.items) === null || _b === void 0 ? void 0 : _b.length) - 1} ${t(((_c = minicart.items) === null || _c === void 0 ? void 0 : _c.length) > 2 ? 'bag.moreitems' : 'bag.moreitem')} `)))),
            React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) }, !window.loggedin && isDesktop && (React.createElement("div", { className: "container-minibag-middle" },
                React.createElement(BagInfoBox, { title: t('checkout.login.text'), text: t('checkout.login.subtitle'), buttonAction: () => {
                        buttonLogin();
                    }, buttonText: t('checkout.logincta'), heading: "h3" })))),
            React.createElement("div", { className: "container-minibag-down" },
                React.createElement("div", { className: "minibag__total" },
                    React.createElement("h3", null, t('bag.esttotal')),
                    React.createElement("p", null, (_e = (_d = minicart.totals) === null || _d === void 0 ? void 0 : _d.subTotal) !== null && _e !== void 0 ? _e : 0.0)),
                window.isClothing && (React.createElement("div", { className: "minibag__available primary-xs" },
                    React.createElement("h3", null, t('bag.available')),
                    React.createElement("p", null, (_f = minicart.totals) === null || _f === void 0 ? void 0 : _f.availablePlafond))),
                window.country === 'RU' && (React.createElement("div", { className: "minibag__disclaimer" },
                    React.createElement("p", { className: "tax-disclaimer", dangerouslySetInnerHTML: { __html: t('bag.taxdisclaimer') } }))),
                minicart.containsMbm && React.createElement("p", { className: "text-disclaimer", dangerouslySetInnerHTML: { __html: t('mbm.bagdisclaimer', { 0: props === null || props === void 0 ? void 0 : props.mbmreturnpolicy }) } }),
                React.createElement("div", { ref: buttonsRef, className: "minibag__buttons", style: { height: buttonsHeight ? `${buttonsHeight}px` : 'auto' } },
                    React.createElement("div", { className: classNames('minibag__buttons__wrapper', { hide: isOpen }) },
                        React.createElement(Button, { type: "button", cls: ['button--primary'], className: "full", onClick: () => {
                                if (minicart.containsMbm && !window.loggedin) {
                                    props === null || props === void 0 ? void 0 : props.onLoginClick();
                                }
                                else {
                                    // analytics.miniBagCheckout();
                                    // TODO da verificare per tema accessibilità
                                    location.href = siteUrl('checkout');
                                }
                            } },
                            React.createElement("span", { ref: useLangChecker }, t('label.checkout'))),
                        !window.noguestcheckout && !window.noexpresscheckout && !minicart.noexpresscheckout && !minicart.items.find((el) => { var _a; return !el.available && !((_a = el === null || el === void 0 ? void 0 : el.availability) === null || _a === void 0 ? void 0 : _a.preorder); }) && (React.createElement(Button, { cls: ['button--secondary'], className: "full", type: 'button', disabled: ((_g = minicart === null || minicart === void 0 ? void 0 : minicart.items) === null || _g === void 0 ? void 0 : _g.find((el) => { var _a; return !el.available && !((_a = el === null || el === void 0 ? void 0 : el.availability) === null || _a === void 0 ? void 0 : _a.preorder); })) != undefined || minicart.checkoutDisabled, onClick: () => {
                                if (minicart.containsMbm && !window.loggedin) {
                                    props === null || props === void 0 ? void 0 : props.onLoginClick();
                                }
                                else {
                                    // analytics.checkoutTypeChose('express checkout');
                                    setModalExpressPaymentsOpen(true);
                                }
                                if (getId() === 'ST') {
                                    analytics.expressCheckout('click', 'minibag');
                                }
                            }, isLoading: modalExpressPaymentsOpen && loaded != 3, ariaExpanded: isOpen, reference: expressCtaRef, "aria-controls": "expressCheckoutMinicart" },
                            React.createElement("span", null, t('checkout.expresscheckout')))),
                        React.createElement(BagPaymentLogos, { mutator: (payment) => !!payment.instalment, onLoad: () => {
                                updateExpressButtonsHeight();
                            } },
                            React.createElement("p", null, t('instalmentpayment.cart.label'))),
                        React.createElement("a", { className: "cta-gotobag button button--link-underline", href: siteUrl('cartUrl') },
                            React.createElement("span", null, t('bag.viewbag')))),
                    React.createElement(ExpressPaymentsMinicart, Object.assign({}, props, { modalExpressPaymentsOpen: modalExpressPaymentsOpen, onClose: () => {
                            setModalExpressPaymentsOpen(false);
                            setTimeout(() => {
                                var _a;
                                (_a = expressCtaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                            }, 250);
                        }, isOpen: isOpen, onLoad: () => setLoaded((value) => ++value), onUpdate: updateExpressButtonsHeight })))))));
};
export default MiniBagModal;
